import { useEffect, useState } from "react"
import { useRouter, useSearchParamsCompat } from "@/lib/router-helpers"
import { Phone } from "lucide-react"
import { useSendOTP } from "@/hooks/use-auth"
import { setPendingPhone } from "@/lib/auth"
import { toast } from "sonner"
import { Toaster } from "@/components/ui/sonner"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/components/auth-provider"
import AuthSkeleton from "@/components/auth-skeleton"

export default function VerifyPhonePage() {
  const router = useRouter()
  const [searchParams] = useSearchParamsCompat()
  const returnUrl = searchParams?.get("returnUrl")
  const [countryCode] = useState("+251")
  const [phone, setPhone] = useState("")
  const sendOTPMutation = useSendOTP()
  const { t } = useTranslation()
  const { isAuthenticated, isLoading } = useAuth()



  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const decodedUrl = returnUrl ? decodeURIComponent(returnUrl) : "/"
      console.log("Verify Page Redirecting - Original:", returnUrl)
      console.log("Verify Page Redirecting - Decoded:", decodedUrl)
      router.push(decodedUrl)
    }
  }, [isLoading, isAuthenticated, router, returnUrl])

  // Show loading state while checking authentication
  if (isLoading) {
    return <AuthSkeleton />
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const cleanPhone = phone.replace(/\D/g, "")

    if (cleanPhone.length === 9 && !cleanPhone.startsWith("9")) {
      toast.error(t("Only Ethio Telecom numbers are supported"))
      return
    }

    if (cleanPhone.length === 10 && !cleanPhone.startsWith("09")) {
      toast.error(t("Only Ethio Telecom numbers are supported"))
      return
    }

    if (cleanPhone.length < 9 || cleanPhone.length > 10) {
      toast.error(t("Please enter a valid phone number"))
      return
    }

    const normalized = `${countryCode}${cleanPhone.startsWith("0") ? cleanPhone.substring(1) : cleanPhone}`

    try {
      console.log('normalized', normalized)
      console.log('phone', phone)
      console.log('returnUrl in verify', returnUrl)
      await sendOTPMutation.mutateAsync({ mobile: normalized })
      setPendingPhone(normalized)
      toast.success(t("OTP sent successfully"))
      const otpUrl = returnUrl ? `/auth/otp?returnUrl=${encodeURIComponent(returnUrl)}` : `/auth/otp`
      console.log('Navigating to otpUrl:', otpUrl)
      router.push(otpUrl)
    } catch (error: any) {
      toast.error(error.message || t("Failed to send OTP"))
    }
  }

  if (!isAuthenticated) {

    return (
      <>
        <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
      `}</style>

        <div className="min-h-[80vh] flex items-center justify-center px-4 pb-8 pt-4 md:pt-12">
          <div className="w-full max-w-lg animate-fade-in-up">
            <div className="rounded-3xl bg-white shadow-xl shadow-pink-50/50 border border-gray-100 p-6 md:p-10 relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600" />
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-60 pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-60 pointer-events-none" />

              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-pink-50 flex items-center justify-center shadow-sm mb-6 group transition-transform duration-500 hover:scale-110 hover:rotate-3">
                  <Phone className="w-9 h-9 text-pink-600 group-hover:text-pink-500 transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 tracking-tight">{t("VerifyYourNumber")}</h3>
                <p className="text-sm text-gray-500 mt-2 max-w-xs mx-auto">
                  {t("We'll send a verification code to your phone.")}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="mt-8 relative z-10">
                <div className="group">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 ml-1">
                    {t("MobileNumber")}
                  </label>
                  <div className="flex items-stretch gap-3">
                    <div className="flex items-center gap-2 px-4 h-12 rounded-xl border border-gray-200 bg-gray-50/50 transition-colors group-hover:border-pink-200">
                      <span className="text-lg">🇪🇹</span>
                      <span className="text-sm font-bold text-gray-900">{countryCode}</span>
                    </div>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="9XXXXXXXX"
                      disabled={sendOTPMutation.isPending}
                      className="w-full flex-1 h-12 rounded-xl border border-gray-200 bg-gray-50/50 px-4 text-gray-900 placeholder-gray-400 transition-all duration-300 focus:bg-white focus:border-pink-500 focus:outline-none focus:ring-4 focus:ring-pink-500/10 group-hover:border-pink-200"
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 ml-1">
                    {t("Enter without country code (e.g., 911223344)")}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={sendOTPMutation.isPending}
                  className="w-full h-12 mt-8 rounded-full bg-gradient-to-r from-pink-600 to-rose-600 text-white text-sm font-bold tracking-wide shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {sendOTPMutation.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("sending")}
                    </span>
                  ) : (
                    t("sendVerificationCode")
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-6 px-4 leading-relaxed">
                  {t("By continuing, you agree to our")}{" "}
                  <span className="text-pink-600 font-bold hover:underline cursor-pointer">{t("Terms of Service")}</span>{" "}
                  {t("and")}{" "}
                  <span className="text-pink-600 font-bold hover:underline cursor-pointer">{t("Privacy Policy")}</span>
                </p>
              </form>
            </div>
          </div>
        </div>
      </>
    )
  }
}
